/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createApi, fetchBaseQuery, type FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react';

import { setKaryaWorkerIdToken } from '../PrepareHeader';
import type {
  GetAssignmentsArgs,
  GetAssignmentsResponse,
  GetNextAvatarJourneyStateArgs,
  GetNextAvatarJourneyStateResponse,
  GetAvatarsWithTasksArgs,
  GetAvatarsWithTasksResponse,
  SubmitAssignmentArgs,
  SubmitAssignmentResponse,
  GetProfileTaskArgs,
  GetProfileTaskResponse,
  UpdateAvatarArgs,
  UpdateAvatarResponse,
  RefuseTaskArgs,
  RefuseTaskResponse,
  SubmitSurveyArgs,
  SubmitSurveyResponse,
} from './Types';
import { resolveBoxUrl } from '../../../utils/url';

// Define a service using a base URL and expected endpoints
export let boxApi: ReturnType<typeof buildBoxApi>;

/**
 * Sets up the Box API with the given fetch function.
 * @param fetchFn - Optional custom fetch function to use for requests.
 * @returns The configured Box API.
 */
export function buildBoxApi(fetchFn?: FetchBaseQueryArgs['fetchFn']) {
  return createApi({
    reducerPath: 'boxserver',
    baseQuery: fetchBaseQuery({
      fetchFn,
      // Attach Karya ID token to headers if available
      prepareHeaders: setKaryaWorkerIdToken,
    }),

    endpoints: (build) => ({
      // Get avatars along with their tasks
      getAvatarsWithTasks: build.query<GetAvatarsWithTasksResponse, GetAvatarsWithTasksArgs>({
        query: ({ baseUrl }) => new URL('/api_worker/v5/avatars_tasks', baseUrl).toString(),
      }),

      // Fetch assignments based on type and pagination
      // Can create new assignments, so use mutation
      getAssignments: build.mutation<GetAssignmentsResponse, GetAssignmentsArgs>({
        query: (args) => ({
          url: resolveBoxUrl(args.access_code, '/api_worker/v5/assignments', {
            type: args.type,
            limit: args.limit?.toString(),
            from: args.from?.toString(),
          }),
          method: 'GET',
          headers: {
            type: args.type,
            access_code: args.access_code,
          },
        }),
      }),

      // Submit completed assignments
      submitAssignment: build.mutation<SubmitAssignmentResponse, SubmitAssignmentArgs>({
        query: ({ access_code, assignments }) => ({
          url: resolveBoxUrl(access_code, '/api_worker/v5/assignments'),
          method: 'PUT',
          body: assignments,
          headers: {
            access_code,
          },
        }),
      }),

      // Get the next journey state of an avatar
      getNextAvatarJourneyState: build.query<GetNextAvatarJourneyStateResponse, GetNextAvatarJourneyStateArgs>({
        query: ({ access_code, currentState }) => ({
          url: resolveBoxUrl(access_code, '/api_worker/v5/next_avatar_journey_state', { currentState }),
          method: 'GET',
          headers: {
            access_code,
          },
        }),
      }),

      /** Update avatar metadata for the current worker. */
      updateAvatar: build.mutation<UpdateAvatarResponse, UpdateAvatarArgs>({
        query: ({ access_code, config, logs }) => ({
          url: resolveBoxUrl(access_code, '/api_worker/v5/update_avatar'),
          method: 'PUT',
          body: {
            access_code,
            config,
            logs,
          },
        }),
      }),

      /** Refuse the task currently assigned to the avatar. */
      refuseTask: build.mutation<RefuseTaskResponse, RefuseTaskArgs>({
        query: ({ access_code }) => ({
          url: resolveBoxUrl(access_code, '/api_worker/v5/refuse_task'),
          method: 'PUT',
          headers: {
            access_code,
          },
        }),
      }),

      /** Submit the task survey response for the avatar. */
      submitSurvey: build.mutation<SubmitSurveyResponse, SubmitSurveyArgs>({
        query: ({ access_code, task_id, survey }) => ({
          url: resolveBoxUrl(access_code, '/api_worker/v5/survey'),
          method: 'POST',
          body: {
            task_id,
            survey,
          },
          headers: {
            access_code,
          },
        }),
      }),

      // Get profile task for the worker
      getProfileTask: build.query<GetProfileTaskResponse, GetProfileTaskArgs>({
        query: ({ baseUrl }) => new URL('/api_worker/v5/profile_task', baseUrl).toString(),
      }),
    }),
  });
}

export function setBoxApi(value: ReturnType<typeof buildBoxApi>) {
  boxApi = value;
}

// Initialize the box and profile box by default
setBoxApi(buildBoxApi());
